import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { InvalidValueMode, ListOption } from '../../../../core/editable-fields/dropdown/dropdown.component';
import { InputMode } from '../../../../core/editable-fields/text-input/text-input.component';
import { ProfileService } from '../../../../core/services/profile.service';
import { UtilityService, toStandardDate, toStandardTime } from '../../../../core/services/utility.service';
import { CourseCatalogService } from '../../../../services/course-catalog.service';
import { SolarWindsService } from '../../../../services/solar-winds.service';
import { RecordingApprovalModalComponent } from './recording-approval-modal/recording-approval-modal.component';

@Component({
    selector: 'msr-recording-requests',
    templateUrl: './msr-recording-requests.component.html',
    styleUrls: ['./msr-recording-requests.component.scss'],
    standalone: false
})

export class MsrRecordingRequestsComponent implements OnInit {
    @Input() pendingRequests: any[];
    @Input() supportRequests: any;
    public now: Date = new Date();
    public isSubmitting: boolean = false;
    public isSearchingSections: boolean = false;
    public personId: number = 0;
    public allLabelCols: number = 4;
    public classrooms: any[] = [];
    public sectionsDict: any = {};
    public isRecordingsCollapsed: boolean = true;

    public request: any = {
        files: [],
        errors$: new BehaviorSubject({}),
        warnings$: new BehaviorSubject({}),
        section: '',
        meetingPattern: '',
        startDate: '',
        endDate: '',
        classStartTime: '',
        classEndTime: '',
        location: '',
        additionalInstructions: '',
    }

    public classrooms$: Observable<any>;
    public sectionQuery$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public submitTicket$: Subject<any> = new Subject<any>; //  The object that is going to the API for submission

    public get inputMode(): typeof InputMode {
        return InputMode;
    }

    public get invalidValueMode(): typeof InvalidValueMode {
        return InvalidValueMode;
    }

    constructor(
        public courseCatalogService: CourseCatalogService,
        public toastr: ToastrService,
        public appService: SolarWindsService,
        public utilityService: UtilityService,
        public profileService: ProfileService,
        private modalService: NgbModal,
    ) {
        this.request.onChange = () => { this.request.IsDirty = true };
        this.request.validate = () => this.validate(this.request);

        this.sectionQuery$.next(new URLSearchParams({ personId: this.profileService.profile.biodata.id }));
    }

    public ngOnInit(): void {
        this.classrooms$ = this.utilityService.lookups$(['SWClassroomSite']).pipe(
            tap((lookups) => this.classrooms = lookups["SWClassroomSite"]),
            shareReplay(1),
        );
    }

    public onSectionSelected(sectionId: number) {
        if (sectionId) {
            const selectedSection = this.sectionsDict[sectionId];

            this.request.meetingPattern = selectedSection.Meeting.Days;
            this.request.startDate = selectedSection.StartDate;
            this.request.endDate = selectedSection.EndDate;
            this.request.classStartTime = selectedSection.Meeting.Start;
            this.request.classEndTime = selectedSection.Meeting.End;
            this.request.location = {
                name: selectedSection.Meeting.Location,
                id: this.classrooms[selectedSection.Meeting.Location]
            }
        }
    }

    private resetForm() {
        this.request = {
            files: [],
            errors$: new BehaviorSubject({}),
            warnings$: new BehaviorSubject({}),
            section: '',
            meetingPattern: '',
            startDate: '',
            endDate: '',
            classStartTime: '',
            classEndTime: '',
            location: '',
            locationId: null,
            additionalInstructions: '',
        }

        this.request.onChange = () => { this.request.IsDirty = true };
        this.request.validate = () => this.validate(this.request);
    }

    public submit() {
        this.isSubmitting = true;
        let formData = new FormData();

        let toSubmit:any = {
            type: 'section',
            sectionId: this.request.section,
            startDate: toStandardDate( new Date(this.request.startDate) ),
            endDate: toStandardDate(new Date(this.request.endDate) ),
            meetingPattern: this.request.meetingPattern,
            classStartTime: toStandardTime(new Date(this.request.classStartTime) ),
            classEndTime: toStandardTime(new Date(this.request.classEndTime)),
            location: this.request.location.name,
            locationId: this.request.location.id,
            message: this.request.additionalInstructions
        }

        formData.append('request', JSON.stringify(toSubmit));
        this.submitTicket$.next(formData);
    }

    public submit$ = this.submitTicket$.pipe(
        switchMap((data) => {
            this.isSubmitting = true;
            return this.appService.createRecording$(data)
        }),
        tap((result) => {
            if (result?.IsSucceeded) {
                this.toastr.success("Section recording request successfully submitted.");
                this.resetForm();
            }
            else {
                let message = "There was an error submitting your request.";

                if (result.error?.Message)
                    message = result.error.Message;

                if (result?.error)
                    message = result.error;

                this.toastr.error(message);
            }

            this.isSubmitting = false;
        }
        ),
    )

    public showRecordingRequest(request: any) {
        const modalRef = this.modalService.open(RecordingApprovalModalComponent, { size: 'lg' });
        modalRef.componentInstance.request = request;

        modalRef.result.then(
            (response) => {
                //  We aren't returning anything because whether it was an approve
                //  or a deny we do the same thing.
                this.appService.refresh();
            },
            (dismissed) => {});
    }


    public validate(obj: any) {
        obj.errors = {};
        obj.warnings = {};
        obj.hasErrors = false;
        var requiredText = 'Required Field';

        if (!obj.startDate?.trim() || (obj.startDate == UtilityService.nullDateString))
            obj.errors['startDate'] = requiredText;

        if (!obj.endDate?.trim() || (obj.endDate == UtilityService.nullDateString))
            obj.errors['endDate'] = requiredText;

        if (!obj.meetingPattern?.trim())
            obj.errors['meetingPattern'] = requiredText;

        if (!obj.classStartTime?.trim())
            obj.errors['classStartTime'] = requiredText;

        if (!obj.classEndTime?.trim())
            obj.errors['classEndTime'] = requiredText;

        if (!obj.section)
            obj.errors['section'] = requiredText;

        var locationName = obj.location?.name ?? obj.location;

        if (!locationName?.trim())
            obj.errors['location'] = requiredText;


        var startDate = new Date(obj.startDate);
        var endDate = new Date(obj.endDate);
        // asuuming the start and end date CAN be the same for one day classes
        if (endDate < startDate)
            obj.errors['endDate'] = 'End Date must be after Start Date.'

        //This is for the new 'Time Only' picker.       
        //for date comparison use same day and combine with start and end times
        const dateString: string = '2025-12-25';
        var startTime = new Date(obj.classStartTime);
        var endTime = new Date(obj.classEndTime);
        const startTimePart: string = startTime.toTimeString().split(' ')[0];
        const endTimePart: string = endTime.toTimeString().split(' ')[0];

        // combine date and time parts
        const combinedStart: string = `${dateString}T${startTimePart}`;
        const combinedEnd: string = `${dateString}T${endTimePart}`;

        // Compare
        var startTimeDate = new Date(combinedStart);
        var endTimeDate = new Date(combinedEnd);
        if (endTimeDate <= startTimeDate)
            obj.errors['classEndTime'] = 'End Time must be after Start Time.'

        obj.syncAlerts?.();

        if (Object.keys(obj.errors).length)
            obj.hasErrors = true;
        else
            obj.hasErrors = false;
    }


    //#region Observables
    public classroomList$(query: string): Observable<any[]> {
        return of(
            Object.keys(this.classrooms)
                .filter(key => key.toLowerCase().includes(query?.toLowerCase()))
                .map(key => ({ name: key, id: this.classrooms[key] }))
        );
    }

    public sections$: Observable<any[]> = this.sectionQuery$.pipe(
        //tap(params => console.log('╔═ sections$', params)),
        tap(() => {
            this.isSearchingSections = true;
            this.sectionsDict = {};
            this.request.section = '';
        }),
        switchMap(params => {
            if (!params)
                return of([]);

            return this.courseCatalogService.sectionDropdownList$(params).pipe(
                tap((sections) => {
                    for (let section of sections)
                        this.sectionsDict[section.SectionId] = section;
                }),
                map((sections) =>
                    sections.map(s => new ListOption(s.DisplayNameStudents, s.SectionId))
                ))
        }),
        tap(() => this.isSearchingSections = false),
        //tap(params => console.log('╚═ sections$', params)),
        shareReplay(1),
    );
    //#endregion
}
